// =============================================================================
// USE ANALYTICS HOOK
// =============================================================================
// React hook for easy analytics integration with automatic tracking

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/services/analytics';

interface UseAnalyticsOptions {
  trackPageView?: boolean;
  trackScrollDepth?: boolean;
  trackTimeOnPage?: boolean;
  scrollDepthThresholds?: number[]; // e.g., [25, 50, 75, 90, 100]
}

const DEFAULT_OPTIONS: UseAnalyticsOptions = {
  trackPageView: true,
  trackScrollDepth: true,
  trackTimeOnPage: true,
  scrollDepthThresholds: [25, 50, 75, 90, 100],
};

export function useAnalytics(
  pageTitle?: string,
  options: UseAnalyticsOptions = DEFAULT_OPTIONS
): typeof analytics {
  const location = useLocation();
  const pageLoadTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const scrollThresholdsReached = useRef<Set<number>>(new Set());
  const timeOnPageInterval = useRef<NodeJS.Timeout | null>(null);

  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Track page views
  useEffect(() => {
    if (opts.trackPageView) {
      const title = pageTitle || document.title;
      analytics.trackPageView(location.pathname, title);
      
      // Reset tracking refs for new page
      pageLoadTime.current = Date.now();
      maxScrollDepth.current = 0;
      scrollThresholdsReached.current.clear();
    }
  }, [location.pathname, pageTitle, opts.trackPageView]);

  // Track scroll depth
  useEffect(() => {
    if (!opts.trackScrollDepth || !opts.scrollDepthThresholds) {
      return;
    }

    const handleScroll = (): void => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round(
        ((scrollTop + windowHeight) / documentHeight) * 100
      );

      if (scrollPercentage > maxScrollDepth.current) {
        maxScrollDepth.current = scrollPercentage;

        // Check if any thresholds were crossed
        opts.scrollDepthThresholds?.forEach((threshold) => {
          if (
            scrollPercentage >= threshold &&
            !scrollThresholdsReached.current.has(threshold)
          ) {
            scrollThresholdsReached.current.add(threshold);
            analytics.trackScrollDepth(threshold, location.pathname);
          }
        });
      }
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [location.pathname, opts.trackScrollDepth, opts.scrollDepthThresholds]);

  // Track time on page
  useEffect(() => {
    if (!opts.trackTimeOnPage) {
      return;
    }

    // Track every 30 seconds
    timeOnPageInterval.current = setInterval(() => {
      const timeOnPage = (Date.now() - pageLoadTime.current) / 1000;
      analytics.trackTimeOnPage(timeOnPage, location.pathname);
    }, 30000);

    return () => {
      if (timeOnPageInterval.current) {
        clearInterval(timeOnPageInterval.current);
      }
      // Track final time on page when leaving
      const finalTimeOnPage = (Date.now() - pageLoadTime.current) / 1000;
      if (finalTimeOnPage > 5) {
        // Only track if spent more than 5 seconds
        analytics.trackTimeOnPage(finalTimeOnPage, location.pathname);
      }
    };
  }, [location.pathname, opts.trackTimeOnPage]);

  return analytics;
}

/**
 * Hook specifically for tracking form interactions
 */
export function useFormAnalytics(formName: string): {
  trackFormStart: () => void;
  trackFormSubmission: (success: boolean) => void;
  trackFieldInteraction: (fieldName: string) => void;
  trackFormAbandonment: (lastField: string) => void;
} {
  const formStarted = useRef(false);
  const lastInteractedField = useRef<string>('');

  const trackFormStart = useCallback((): void => {
    if (!formStarted.current) {
      analytics.trackFormStart(formName);
      formStarted.current = true;
    }
  }, [formName]);

  const trackFormSubmission = useCallback(
    (success: boolean): void => {
      analytics.trackFormSubmission(formName, success);
      formStarted.current = false;
    },
    [formName]
  );

  const trackFieldInteraction = useCallback(
    (fieldName: string): void => {
      trackFormStart(); // Ensure form start is tracked
      lastInteractedField.current = fieldName;
      analytics.trackFormFieldInteraction(formName, fieldName);
    },
    [formName, trackFormStart]
  );

  const trackFormAbandonment = useCallback((): void => {
    if (formStarted.current && lastInteractedField.current) {
      analytics.trackFormAbandonment(formName, lastInteractedField.current);
    }
  }, [formName]);

  // Track abandonment on component unmount
  useEffect(() => {
    return () => {
      trackFormAbandonment();
    };
  }, [trackFormAbandonment]);

  return {
    trackFormStart,
    trackFormSubmission,
    trackFieldInteraction,
    trackFormAbandonment,
  };
}

/**
 * Hook for tracking button clicks with automatic context
 */
export function useButtonAnalytics(buttonName: string, location?: string): () => void {
  const currentLocation = useLocation();
  const actualLocation = location || currentLocation.pathname;

  return useCallback((): void => {
    analytics.trackButtonClick(buttonName, actualLocation);
  }, [buttonName, actualLocation]);
}

